import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import config from '../../../config';

const PAY_MODES = ['Cash', 'Bank', 'UPI', 'Cheque'];

// HR Salary Payments — ledger of every payroll payment. Filters by month
// + employee. Recording a payment lives on the Payroll page (Pay button)
// since it needs the payroll row's context; here we just view + delete.
export default function SalaryPayments() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const now = new Date();

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [empFilter, setEmpFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Employees/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('month', month);
      qs.append('year', year);
      if (empFilter) qs.append('employeeId', empFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/SalaryPayments/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load payments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, month, year, empFilter]);

  const filtered = useMemo(
    () => (modeFilter ? rows.filter((r) => r.payMode === modeFilter) : rows),
    [rows, modeFilter],
  );

  const totals = useMemo(() => {
    return {
      count: filtered.length,
      amount: filtered.reduce((a, r) => a + Number(r.amount || 0), 0),
      byMode: PAY_MODES.reduce((acc, m) => {
        acc[m] = filtered
          .filter((r) => r.payMode === m)
          .reduce((a, r) => a + Number(r.amount || 0), 0);
        return acc;
      }, {}),
    };
  }, [filtered]);

  const remove = async (r) => {
    if (
      !window.confirm(
        `Delete ₹${r.amount} payment to ${r.employeeName} on ${new Date(r.paymentDate).toLocaleDateString('en-IN')}?`,
      )
    )
      return;
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/SalaryPayment/${r.id}/${companyId}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      toast('Payment deleted');
      load();
    } else toast('Delete failed', 'error');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: '#0891b2',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <PaymentsIcon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
            Salary Payments
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
            HR · {totals.count} payment(s) · ₹
            {totals.amount.toLocaleString('en-IN')} disbursed
          </Typography>
        </Box>
      </Stack>

      {/* By-mode KPI cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {PAY_MODES.map((m, i) => {
          const colors = ['#16a34a', '#1A56DB', '#8b5cf6', '#f59e0b'];
          return (
            <Grid item xs={6} sm={3} key={m}>
              <Card sx={{ borderLeft: `4px solid ${colors[i]}`, p: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                  {m}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                  ₹{Number(totals.byMode[m] || 0).toLocaleString('en-IN')}
                </Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}>
        <TextField
          select
          size='small'
          label='Month'
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 140 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <MenuItem key={m} value={m}>
              {new Date(2000, m - 1, 1).toLocaleString('en-IN', {
                month: 'long',
              })}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type='number'
          size='small'
          label='Year'
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ bgcolor: '#fff', width: 110 }}
        />
        <TextField
          select
          size='small'
          label='Employee'
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.employeeName} ({e.employeeCode})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label='Mode'
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 140 }}>
          <MenuItem value=''>All</MenuItem>
          {PAY_MODES.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Paid By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No payments in this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        {new Date(r.paymentDate).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.employeeName}
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {r.employeeCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(r.year, r.month - 1, 1).toLocaleString('en-IN', {
                          month: 'short',
                        })}{' '}
                        {r.year}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.payMode}
                          sx={{
                            bgcolor: '#f1f5f9',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {r.referenceNo || '—'}
                        {r.bankName && (
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {r.bankName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{r.paidByName || '—'}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700, color: '#16a34a' }}>
                        ₹{Number(r.amount).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ color: '#475569', fontSize: '0.78rem' }}>
                        {r.remarks || '—'}
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Delete (reverses payroll status)'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
